<template>
  <div class="diff-view">
    <div class="diff-field" v-for="change in changes" :key="change.field">
      <label class="diff-label">
        <i class="fa fa-pencil"></i>
        {{ change.label }}
        <span class="diff-stats" v-if="change.html && change.stats">
          ({{ change.stats }})
        </span>
      </label>

      <!-- Unified diff for HTML content fields -->
      <div v-if="change.html" class="unified-diff-content" v-html="getUnifiedDiff(change)"></div>

      <!-- Simple before/after for non-HTML fields -->
      <div v-else class="simple-diff">
        <div class="simple-diff-row" v-if="change.displayOldValue !== change.displayNewValue">
          <span class="diff-removed" v-if="change.displayOldValue">{{ change.displayOldValue }}</span>
          <span class="diff-arrow" v-if="change.displayOldValue && change.displayNewValue">
            <i class="fa fa-long-arrow-right"></i>
          </span>
          <span class="diff-added" v-if="change.displayNewValue">{{ change.displayNewValue }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { mapStores } from 'pinia'
  import { useUsersStore } from '@/stores/users'
  import {
    AUDIT_FIELD_LABELS,
    AUDIT_HTML_FIELDS,
    AUDIT_BOOLEAN_FIELDS,
    humanizeFieldName
  } from '@/constants/audit_constants'

  export default {
    props: {
      auditedChanges: {
        type: Object,
        required: true
      },
      fieldLabels: {
        type: Object,
        default: () => AUDIT_FIELD_LABELS
      },
      htmlFields: {
        type: Array,
        default: () => AUDIT_HTML_FIELDS
      },
      maxLength: {
        type: Number,
        default: 500
      }
    },

    computed: {
      ...mapGetters('squeues', { squeueForId: 'forId' }),
      ...mapStores(useUsersStore),

      changes() {
        if (!this.auditedChanges) return []

        return Object.entries(this.auditedChanges).map(([field, change]) => {
          // For 'update' action, change is [old_value, new_value]
          // For 'create' action, change is just the new value
          const isArray = Array.isArray(change)
          const oldValue = isArray ? change[0] : null
          const newValue = isArray ? change[1] : change
          const isHtml = this.htmlFields.includes(field)

          // For HTML fields, calculate lengths and stats
          const oldText = isHtml && oldValue ? this.stripHtml(oldValue) : null
          const newText = isHtml && newValue ? this.stripHtml(newValue) : null
          const oldLength = oldText ? oldText.length : 0
          const newLength = newText ? newText.length : 0
          
          let stats = null
          if (isHtml && oldValue && newValue) {
            const diff = newLength - oldLength
            if (diff > 0) {
              stats = `+${diff} chars`
            } else if (diff < 0) {
              stats = `${diff} chars`
            } else {
              stats = 'modified'
            }
          }

          return {
            field,
            label: this.fieldLabels[field] || this.humanizeField(field),
            oldValue,
            newValue,
            displayOldValue: this.formatValue(field, oldValue),
            displayNewValue: this.formatValue(field, newValue),
            html: isHtml,
            oldLength,
            newLength,
            oldText,
            newText,
            stats
          }
        })
      }
    },

    methods: {
      humanizeField: humanizeFieldName,

      formatValue(field, value) {
        if (value === null || value === undefined) return null

        // Handle special fields that need lookup
        if (field === 'squeue_id') {
          const squeue = this.squeueForId(value)
          return squeue ? squeue.title : `Queue #${value}`
        }

        if (field === 'user_id') {
          const user = this.usersStore.forId(value)
          return user ? user.name : `User #${value}`
        }

        // Handle boolean fields
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No'
        }

        // Handle numeric booleans using shared constant
        if (AUDIT_BOOLEAN_FIELDS.includes(field)) {
          return value ? 'Yes' : 'No'
        }

        return value
      },

      stripHtml(html) {
        if (!html) return ''
        const div = document.createElement('div')
        div.innerHTML = html
        return div.textContent || div.innerText || ''
      },

      formatCharCount(count) {
        if (count >= 1000) {
          return `${(count / 1000).toFixed(1)}k chars`
        }
        return `${count} chars`
      },

      getUnifiedDiff(change) {
        const oldText = change.oldText || ''
        const newText = change.newText || ''

        // Handle empty cases
        if (!oldText && newText) {
          // All new content
          return `<span class="diff-added">${this.escapeHtml(this.truncate(newText, 800))}</span>`
        }
        if (oldText && !newText) {
          // All deleted content
          return `<span class="diff-removed">${this.escapeHtml(this.truncate(oldText, 800))}</span>`
        }
        if (!oldText && !newText) {
          return '<em class="text-muted">(empty)</em>'
        }

        // Find common prefix
        let prefixEnd = 0
        const minLen = Math.min(oldText.length, newText.length)
        while (prefixEnd < minLen && oldText[prefixEnd] === newText[prefixEnd]) {
          prefixEnd++
        }

        // Find common suffix (but not overlapping with prefix)
        let oldSuffixStart = oldText.length
        let newSuffixStart = newText.length
        while (
          oldSuffixStart > prefixEnd && 
          newSuffixStart > prefixEnd && 
          oldText[oldSuffixStart - 1] === newText[newSuffixStart - 1]
        ) {
          oldSuffixStart--
          newSuffixStart--
        }

        // Extract the parts
        const commonPrefix = oldText.substring(0, prefixEnd)
        const removedMiddle = oldText.substring(prefixEnd, oldSuffixStart)
        const addedMiddle = newText.substring(prefixEnd, newSuffixStart)
        const commonSuffix = oldText.substring(oldSuffixStart)

        // Build the unified diff display
        let result = ''
        
        // Show truncated prefix with context
        const maxContext = 150
        if (commonPrefix.length > maxContext) {
          result += `<span class="diff-context">...${this.escapeHtml(commonPrefix.slice(-maxContext))}</span>`
        } else if (commonPrefix) {
          result += `<span class="diff-context">${this.escapeHtml(commonPrefix)}</span>`
        }

        // Show removed content
        if (removedMiddle) {
          const truncatedRemoved = removedMiddle.length > 400 
            ? removedMiddle.substring(0, 400) + `... [${removedMiddle.length - 400} more chars removed]`
            : removedMiddle
          result += `<span class="diff-removed">${this.escapeHtml(truncatedRemoved)}</span>`
        }

        // Show added content
        if (addedMiddle) {
          const truncatedAdded = addedMiddle.length > 400 
            ? addedMiddle.substring(0, 400) + `... [${addedMiddle.length - 400} more chars added]`
            : addedMiddle
          result += `<span class="diff-added">${this.escapeHtml(truncatedAdded)}</span>`
        }

        // Show truncated suffix with context
        if (commonSuffix.length > maxContext) {
          result += `<span class="diff-context">${this.escapeHtml(commonSuffix.substring(0, maxContext))}...</span>`
        } else if (commonSuffix) {
          result += `<span class="diff-context">${this.escapeHtml(commonSuffix)}</span>`
        }

        return result || '<em class="text-muted">(no visible changes)</em>'
      },

      truncate(value, maxLen) {
        const max = maxLen || this.maxLength
        if (!value) return ''
        const str = String(value)
        if (str.length <= max) return str
        return str.substring(0, max) + '...'
      },

      escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
      }
    }
  }
</script>

<style lang="scss" scoped>
  .diff-view {
    max-height: 400px;
    overflow-y: auto;
  }

  .diff-field {
    margin-bottom: 16px;
    border: 1px solid #ddd;
    border-radius: 4px;
    overflow: hidden;
  }

  .diff-label {
    display: block;
    padding: 8px 12px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;
    font-weight: bold;
    font-size: 12px;
    color: #666;
    margin: 0;

    i {
      color: #8a6d3b;
      margin-right: 4px;
    }
  }

  .diff-stats {
    font-weight: normal;
    font-size: 11px;
    color: #8a6d3b;
    margin-left: 4px;
  }

  // Unified diff styles
  .unified-diff-content {
    padding: 12px;
    font-size: 13px;
    line-height: 1.6;
    word-wrap: break-word;
    white-space: pre-wrap;
    max-height: 300px;
    overflow-y: auto;
    background-color: #fafafa;
  }

  // Simple diff for non-HTML fields
  .simple-diff {
    padding: 12px;
    font-size: 13px;
    background-color: #fafafa;
  }

  .simple-diff-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .diff-arrow {
    color: #666;
    i {
      font-size: 14px;
    }
  }

  // Diff highlighting colors - shared variables
  $diff-removed-bg: #fdb8c0;
  $diff-added-bg: #acf2bd;
  $diff-removed-strike: #cb2431;

  // :deep() selectors style content injected via v-html in unified diff view
  :deep(.diff-context) {
    color: #666;
  }

  :deep(.diff-removed),
  .diff-removed {
    background-color: $diff-removed-bg;
    padding: 2px 6px;
    border-radius: 2px;
    text-decoration: line-through;
    text-decoration-color: $diff-removed-strike;
  }

  :deep(.diff-added),
  .diff-added {
    background-color: $diff-added-bg;
    padding: 2px 6px;
    border-radius: 2px;
  }
</style>
